import { toast } from "$global-providers/toast-provider";
import type { ApolloError } from "@apollo/client";
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  styled,
  Typography,
} from "@material-ui/core";
import { EpocusRole } from "elephant-web/gql";
import { phone } from "phone";
import { useState } from "react";
import {
  EPOCUS_TEXT_PRIMARY,
  EPOCUS_TEXT_SECONDARY,
  EPOCUS_TO_DOC_BG,
  EPOCUS_TO_DOC_BORDER,
  rem,
} from "../../_lib/epocus-palette";
import {
  ProgramUserForm,
  type ProgramUserFormValues,
} from "../../settings/users/_lib/program-user-form";
import { useEpocusAdminProvisionUserMutation } from "./__generated__/epocus-admin-operations.gql";

const Intro = styled(Typography)({
  fontSize: rem(13),
  color: EPOCUS_TEXT_SECONDARY,
  marginBottom: rem(16),
});

const Refusal = styled(Box)({
  backgroundColor: EPOCUS_TO_DOC_BG,
  border: `1px solid ${EPOCUS_TO_DOC_BORDER}`,
  borderRadius: 12,
  padding: 16,
  marginBottom: rem(16),
});

const RefusalText = styled(Typography)({
  fontSize: rem(13),
  color: EPOCUS_TEXT_PRIMARY,
});

const refusalMessage = ({ message }: ApolloError): string => {
  if (message.includes("setEpocusUserEnabled")) {
    return "That email already has an ePOCUS login and it is disabled. Re-enable the existing account rather than creating a second one.";
  }
  if (message.includes("deleted ePOCUS user")) {
    return "That email belonged to an ePOCUS user that has been deleted, and the deleted record still holds the address. It can't be used again until that record is dealt with.";
  }
  if (message.includes("already has an ePOCUS user")) {
    return "That email already has an ePOCUS user. Add them to a program from that program's page rather than creating a second account.";
  }
  return `Couldn't create the program admin: ${message}`;
};

export const CreateProgramAdminDialog: FC<{
  open: boolean;
  onClose: () => void;
}> = ({ open, onClose }) => {
  const [refusal, setRefusal] = useState<string>();

  const handleClose = () => {
    setRefusal(undefined);
    onClose();
  };

  const [provisionUser, { loading }] = useEpocusAdminProvisionUserMutation({
    onCompleted: ({ provisionEpocusUser }) => {
      toast.success(
        `${provisionEpocusUser.firstName} ${provisionEpocusUser.lastName} can now sign in — they'll get an email to set their password`,
      );
      handleClose();
    },
    onError: (error) => setRefusal(refusalMessage(error)),
  });

  const handleSubmit = async (values: ProgramUserFormValues) => {
    setRefusal(undefined);
    const telephone = values.telephone.trim();
    await provisionUser({
      variables: {
        input: {
          email: values.email,
          firstName: values.firstName,
          lastName: values.lastName,
          telephone: telephone
            ? phone(telephone).phoneNumber || telephone
            : undefined,
          role: EpocusRole.ProgramAdmin,
        },
      },
    });
  };

  return (
    <Dialog open={open} onClose={handleClose} fullWidth maxWidth="sm">
      <DialogTitle>New program admin</DialogTitle>
      <DialogContent>
        <Intro>
          They'll get an email to set their password and can sign in straight
          away. Add them to a program once it exists.
        </Intro>
        {refusal && (
          <Refusal role="alert">
            <RefusalText>{refusal}</RefusalText>
          </Refusal>
        )}
        <ProgramUserForm
          submitLabel="Create program admin"
          submitting={loading}
          fixedRole={EpocusRole.ProgramAdmin}
          onSubmit={handleSubmit}
        />
      </DialogContent>
      <DialogActions>
        <Button onClick={handleClose} style={{ textTransform: "none" }}>
          Cancel
        </Button>
      </DialogActions>
    </Dialog>
  );
};
